import { useMemo } from 'react';

import { useRouter } from 'next/navigation';

import { UserType } from '@enums/index';
import { useAppSelector } from '@hooks/store';
import { useLazyGetMeteePlansQuery } from '@modules/billings/api';
import { MenteesPlanStatus } from '@modules/billings/enums';
import { paths } from '@routes/path';
import { useMenteeLoginMutation, useMentorLoginMutation } from '../api';

import { LoginRequest } from '../types';

interface LoginProps {
    userType: UserType;
}

export default function useLogin({ userType }: LoginProps) {
    const router = useRouter();
    const [mentorLogin, { isLoading: isMentorLoading }] = useMentorLoginMutation();
    const [menteeLogin, { isLoading: IsMenteeLoading }] = useMenteeLoginMutation();

    const [fetchPlans] = useLazyGetMeteePlansQuery();

    const redirectPath = useAppSelector(state => state.reducer.booking.redirectPath);

    const isLoading = useMemo(
        () => isMentorLoading || IsMenteeLoading,
        [isMentorLoading, IsMenteeLoading]
    );

    const handleMentorLogin = async (payload: LoginRequest) => {
        const response = await mentorLogin(payload);
        if ('data' in response) {
            router.push(paths.mentor.dashboard.home);
        } else {
            return response.error;
        }
    };

    const handleMenteeLogin = async (payload: LoginRequest) => {
        const response = await menteeLogin(payload);

        if ('data' in response) {
            if (redirectPath) {
                const { data: plansData } = await fetchPlans({
                    offset: 0,
                    limit: 1000,
                    status: MenteesPlanStatus.Active,
                });

                const hasActivePlan = (plansData?.menteePlans || []).some(
                    plan => plan.totalCredits > plan.usedCredits
                );
                router.push(hasActivePlan ? redirectPath : paths.general.pricing);
            } else {
                router.push(paths.mentee.dashboard.home);
            }
        }
        return response;
    };

    const handleLogin = async (payload: LoginRequest) => {
        switch (userType) {
            case UserType.Mentor:
                await handleMentorLogin(payload);
                break;
            case UserType.Mentee:
                await handleMenteeLogin(payload);
                break;
            default:
                break;
        }
    };
    return {
        handleLogin,
        isLoading,
    };
}
